import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { InstallGitHubApp } from '@/components/github/InstallGitHubApp';
import { RepositoryList } from '@/components/github/RepositoryList';
import { SyncJobsMonitor } from '@/components/github/SyncJobsMonitor';
import { trpc } from '@/lib/trpc';
import { useAtom } from 'jotai'
import { activeWorkspaceWithStorageAtom } from '@/stores/workspaceStore';
import {
  GitBranch,
  Settings,
  Activity,
  Info,
  RefreshCw,
  CheckCircle2,
  XCircle,
  AlertCircle,
  ArrowLeft,
  Github
} from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { APP_NAME } from '@/lib/env';
export const Route = createFileRoute('/(protected)/workspace/integrations/github')({
  component: GitHubIntegrationPage,
});

function GitHubIntegrationPage() {
  const [workspace] = useAtom(activeWorkspaceWithStorageAtom)
  const [selectedRepoId, setSelectedRepoId] = useState<string | undefined>();
  const [activeTab, setActiveTab] = useState('repositories');

  // Check if GitHub App is configured
  const { data: isConfigured, isLoading: isCheckingConfig } = trpc.githubApp.isConfigured.useQuery();

  // Get connected repositories for this workspace
  const { data: connectedRepos } = trpc.githubApp.listConnectedRepositories.useQuery(
    { workspaceId: workspace?.id! },
    { enabled: !!workspace?.id && isConfigured === true }
  );

  // Get sync statistics for the workspace
  const { data: syncStats } = trpc.githubApp.getSyncStatistics.useQuery(
    { workspaceId: workspace?.id! },
    { enabled: !!workspace?.id && isConfigured === true }
  );

  const handleRepoSelect = (repoId: string) => {
    setSelectedRepoId(repoId);
    setActiveTab('sync-jobs');
  };

  if (isCheckingConfig) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center space-x-2 text-sm text-muted-foreground mb-2">
            <Link
              to="/workspace/integrations"
              className="flex items-center hover:text-foreground transition"
            >
              <ArrowLeft className="h-3 w-3 mr-1" />
              Integrations
            </Link>
            <span>/</span>
            <span>GitHub</span>
          </div>
          <h1 className="text-3xl font-bold flex items-center space-x-2">
            <Github className="h-8 w-8" />
            <span>GitHub Integration</span>
          </h1>
          <p className="text-muted-foreground mt-2">
            Connect GitHub repositories to sync documentation and track changes
          </p>
        </div>

        <div className="flex items-center space-x-2">
          {isConfigured ? (
            <Badge variant="secondary" className="flex items-center space-x-1">
              <CheckCircle2 className="h-3 w-3" />
              <span>Configured</span>
            </Badge>
          ) : (
            <Badge variant="destructive" className="flex items-center space-x-1">
              <XCircle className="h-3 w-3" />
              <span>Not Configured</span>
            </Badge>
          )}
        </div>
      </div>

      {/* Alert for non-configured state */}
      {!isConfigured && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            GitHub App integration is not configured. Contact your administrator to enable GitHub integration.
          </AlertDescription>
        </Alert>
      )}

      {/* Main Content */}
      {isConfigured && (
        <>
          {/* Statistics Overview */}
          {syncStats && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardHeader className="pb-3">
                  <div className="text-sm font-medium">Connected Repositories</div>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{connectedRepos?.length || 0}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <div className="text-sm font-medium">Total Syncs</div>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{syncStats.totalSyncs || 0}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <div className="text-sm font-medium">Success Rate</div>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {syncStats.totalSyncs > 0
                      ? Math.round((syncStats.successfulSyncs / syncStats.totalSyncs) * 100)
                      : 0}%
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <div className="text-sm font-medium">Files Processed</div>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{syncStats.filesProcessed || 0}</div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
            <TabsList className="grid grid-cols-4 w-full max-w-[600px]">
              <TabsTrigger value="repositories">
                <GitBranch className="h-4 w-4 mr-2" />
                Repositories
              </TabsTrigger>
              <TabsTrigger value="sync-jobs">
                <Activity className="h-4 w-4 mr-2" />
                Sync Jobs
              </TabsTrigger>
              <TabsTrigger value="settings">
                <Settings className="h-4 w-4 mr-2" />
                Settings
              </TabsTrigger>
              <TabsTrigger value="installation">
                <Info className="h-4 w-4 mr-2" />
                Installation
              </TabsTrigger>
            </TabsList>

            {/* Repositories Tab */}
            <TabsContent value="repositories" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Repository Management</CardTitle>
                  <CardDescription>
                    Connect and manage GitHub repositories for your workspace
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <RepositoryList
                    workspaceId={workspace?.id!}
                    workspaceName={workspace?.name}
                  />
                </CardContent>
              </Card>
            </TabsContent>

            {/* Sync Jobs Tab */}
            <TabsContent value="sync-jobs" className="space-y-4">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Repository Selector */}
                <div className="lg:col-span-1">
                  <Card className="h-full">
                    <CardHeader>
                      <CardTitle className="text-base">Select Repository</CardTitle>
                      <CardDescription className="text-xs">
                        Choose a repository to view its sync history
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ScrollArea className="h-[400px]">
                        <div className="space-y-2">
                          {connectedRepos?.map((repo) => (
                            <Button
                              key={repo.id}
                              variant={selectedRepoId === repo.id ? 'secondary' : 'outline'}
                              className="w-full justify-start text-left"
                              onClick={() => handleRepoSelect(repo.id)}
                            >
                              <div className="flex flex-col items-start">
                                <span className="font-mono text-sm">{repo.fullName}</span>
                                <span className="text-xs text-muted-foreground">
                                  {repo.syncBranch || repo.defaultBranch}
                                </span>
                              </div>
                            </Button>
                          ))}
                          {(!connectedRepos || connectedRepos.length === 0) && (
                            <p className="text-sm text-muted-foreground text-center py-8">
                              No repositories connected yet
                            </p>
                          )}
                        </div>
                      </ScrollArea>
                    </CardContent>
                  </Card>
                </div>

                {/* Sync Jobs Monitor */}
                <div className="lg:col-span-2">
                  <SyncJobsMonitor
                    repositoryId={selectedRepoId}
                    workspaceId={workspace?.id}
                  />
                </div>
              </div>
            </TabsContent>

            {/* Settings Tab */}
            <TabsContent value="settings" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Sync Configuration</CardTitle>
                  <CardDescription>
                    Configure how repositories are synchronized with your workspace
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-sm font-medium mb-2">File Patterns</h4>
                      <p className="text-sm text-muted-foreground mb-3">
                        Specify which files to include or exclude from synchronization
                      </p>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between p-3 border rounded">
                          <span className="text-sm font-mono">*.md, *.mdx</span>
                          <Badge>Include</Badge>
                        </div>
                        <div className="flex items-center justify-between p-3 border rounded">
                          <span className="text-sm font-mono">node_modules/**</span>
                          <Badge variant="secondary">Exclude</Badge>
                        </div>
                        <div className="flex items-center justify-between p-3 border rounded">
                          <span className="text-sm font-mono">.git/**</span>
                          <Badge variant="secondary">Exclude</Badge>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h4 className="text-sm font-medium mb-2">Sync Triggers</h4>
                      <p className="text-sm text-muted-foreground mb-3">
                        Choose when to automatically sync repositories
                      </p>
                      <div className="space-y-2">
                        <label className="flex items-center space-x-2">
                          <input type="checkbox" className="rounded" defaultChecked />
                          <span className="text-sm">On push to default branch</span>
                        </label>
                        <label className="flex items-center space-x-2">
                          <input type="checkbox" className="rounded" defaultChecked />
                          <span className="text-sm">On pull request merge</span>
                        </label>
                        <label className="flex items-center space-x-2">
                          <input type="checkbox" className="rounded" />
                          <span className="text-sm">On release published</span>
                        </label>
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h4 className="text-sm font-medium mb-2">Processing Options</h4>
                      <p className="text-sm text-muted-foreground mb-3">
                        Configure how synchronized content is processed
                      </p>
                      <div className="space-y-2">
                        <label className="flex items-center space-x-2">
                          <input type="checkbox" className="rounded" defaultChecked />
                          <span className="text-sm">Generate AI summaries</span>
                        </label>
                        <label className="flex items-center space-x-2">
                          <input type="checkbox" className="rounded" defaultChecked />
                          <span className="text-sm">Extract code snippets</span>
                        </label>
                        <label className="flex items-center space-x-2">
                          <input type="checkbox" className="rounded" />
                          <span className="text-sm">Create documentation hierarchy</span>
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end space-x-2 pt-4">
                    <Button variant="outline">Reset to Defaults</Button>
                    <Button>Save Configuration</Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Installation Tab */}
            <TabsContent value="installation" className="space-y-4">
              <InstallGitHubApp />

              <Card>
                <CardHeader>
                  <CardTitle>About GitHub Integration</CardTitle>
                  <CardDescription> 
                    How the GitHub App integration works with {APP_NAME}   
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium mb-2">Features</h4>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      <li>• Automatic synchronization of documentation from repositories</li>
                      <li>• Real-time webhook updates on repository changes</li>
                      <li>• Support for public and private repositories</li>
                      <li>• Branch-specific documentation syncing</li>
                      <li>• File pattern filtering for selective sync</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium mb-2">Permissions</h4>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      <li>• Read access to repository contents and metadata</li>
                      <li>• Webhook events for push, pull request, and release events</li>
                      <li>• No write access to your repositories</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium mb-2">Security</h4>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      <li>• All webhook payloads are verified using HMAC signatures</li>
                      <li>• API tokens are encrypted and stored securely</li>
                      <li>• Repository access is scoped to selected installations only</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}